import { useMemo, useState } from "react";
import { Icon } from "../ui/Icon";
import "./Resource.css";

/**
 * Kaynak yükü ısı haritası — Faz 4.
 *
 * Kişi × gün tablosu; hücre rengi kapasiteye ORAN ile koyulaşır. Renk TEK BAŞINA
 * anlatmaz (CLAUDE.md kural 5): aşan hücrede "!" işareti + kalın yazı + kenarlık,
 * kişi satırında ise aşım gün sayısını yazan bir etiket bulunur.
 */

export interface HeatDay { day: string; hours: number; over: boolean; taskIds: string[] }
export interface HeatPerson { userId: string; name: string; days: HeatDay[]; totalHours: number; overDays: number; peakHours: number }

interface Props {
  /** Sunucudan gelen kapasite. Veri yoksa `null` — uydurma değer GÖSTERİLMEZ. */
  capacityHours: number | null;
  people: HeatPerson[];
  totals: Array<{ day: string; hours: number }>;
  missingEstimate: Array<{ id: string; title: string }>;
  unassigned: Array<{ id: string; title: string }>;
  unplanned: Array<{ id: string; title: string }>;
  noWorkdays: Array<{ id: string; title: string }>;
  badRange: Array<{ id: string; title: string }>;
  weeks: number;
  onWeeksChange: (n: number) => void;
  onOpenTask: (id: string) => void;
  loading: boolean;
  error: string | null;
}

const DOW = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];

function label(day: string): { d: string; dow: string; month: string } {
  const [y, m, dd] = day.split("-").map(Number);
  const dt = new Date(y!, m! - 1, dd!);
  return { d: String(dt.getDate()), dow: DOW[dt.getDay()]!, month: `${dt.getDate()}.${dt.getMonth() + 1}` };
}

type WarnKey = "missing" | "unassigned" | "unplanned" | "noWorkdays" | "badRange";

export function ResourceHeatmap({
  capacityHours, people, totals, missingEstimate, unassigned, unplanned, noWorkdays, badRange,
  weeks, onWeeksChange, onOpenTask, loading, error,
}: Props) {
  const [openList, setOpenList] = useState<WarnKey | null>(null);
  // Yüke KATILAMAYAN her görev sınıfı için ayrı uyarı — sessizce düşürmek yasak.
  const warnGroups = ([
    { key: "missing", items: missingEstimate, label: "tahmini süre yok",
      hint: "Bu görevler kaynak yüküne KATILMIYOR — tahmini süre girilmeden yük hesaplanamaz." },
    { key: "unassigned", items: unassigned, label: "sorumlu yok",
      hint: "Bu görevler kimseye yazılamıyor — “Sorumlu” rolünde biri atanmadan yük hesaplanamaz." },
    { key: "unplanned", items: unplanned, label: "tarih yok",
      hint: "Planlanan başlangıç/bitiş girilmeden görevin hangi güne düşeceği bilinemez." },
    { key: "noWorkdays", items: noWorkdays, label: "çalışma günü yok",
      hint: "Görevin tüm aralığı tatil/hafta sonuna düşüyor — saatleri hiçbir güne yazılamıyor. Tarihleri kaydırın ya da çalışma takvimini gözden geçirin." },
    { key: "badRange", items: badRange, label: "tarih aralığı hatalı",
      hint: "Tarih aralığı makul sınırın ötesinde (ör. yıl alanına yanlış giriş). Görevin tarihlerini düzeltin." },
  ] as Array<{ key: WarnKey; items: Array<{ id: string; title: string }>; label: string; hint: string }>)
    .filter((g) => g.items.length > 0);
  const days = useMemo(() => totals.map((t) => t.day), [totals]);
  const overPeople = people.filter((p) => p.overDays > 0).length;

  /** Yoğunluk 0–1: kapasitenin katı olarak (2× ve üstü en koyu). */
  const level = (h: number) => (!capacityHours || capacityHours <= 0 ? 0 : Math.min(1, h / (capacityHours * 2)));

  return (
    <div className="res">
      <div className="res-bar">
        <div className="gnt-scale" role="group" aria-label="Aralık">
          {[2, 4, 8].map((w) => (
            <button key={w} type="button" className={`gnt-scale-btn ${weeks === w ? "active" : ""}`}
              onClick={() => onWeeksChange(w)}>{w} hafta</button>
          ))}
        </div>
        {/* Kapasite YALNIZ sunucudan geldiyse yazılır. Hata/yükleme sırasında
            uydurma bir "8 sa" göstermek, kullanıcıya doğrulanmamış bir rakamı
            kesin bilgi gibi sunuyordu (tesisin kapasitesi 7,5 olabilir). */}
        {capacityHours != null ? (
          <span className="res-cap">Günlük kapasite: <b className="mono">{capacityHours} sa</b></span>
        ) : null}
        <div className="grow" />
        {overPeople > 0 ? (
          <span className="badge-pill b-danger" title="Kapasitesi aşılan kişi sayısı">
            <Icon name="alertTriangle" size={11} />{overPeople} kişi aşırı yüklü
          </span>
        ) : null}
        {warnGroups.map((g) => (
          <button key={g.key} type="button" className="res-warn"
            onClick={() => setOpenList(openList === g.key ? null : g.key)}>
            <Icon name="alertTriangle" size={11} />{g.items.length} görevde {g.label}
          </button>
        ))}
      </div>

      {/* Sayıyı gösterip HANGİ görev olduğunu saklamak kullanıcıyı çaresiz
          bırakıyordu; tıklayınca görevlere gidilir. */}
      {openList && warnGroups.some((g) => g.key === openList) ? (() => {
        const g = warnGroups.find((x) => x.key === openList)!;
        return (
          <div className="res-warnlist">
            <b>Yüke katılamayan görevler — {g.label}</b>
            <p className="res-warnhint">{g.hint}</p>
            <div className="res-warnitems">
              {g.items.map((t) => (
                <button key={t.id} type="button" className="res-warnitem" onClick={() => onOpenTask(t.id)}>{t.title}</button>
              ))}
            </div>
          </div>
        );
      })() : null}

      {error ? (
        <div className="form-banner-err res-msg">Kaynak yükü yüklenemedi: {error}</div>
      ) : loading ? (
        <p className="res-msg">Yükleniyor…</p>
      ) : people.length === 0 ? (
        <div className="empty-state">
          <span className="ic" aria-hidden="true"><Icon name="users" size={28} /></span>
          <p className="ttl">Bu aralıkta yük yok</p>
          <p className="sub">
            Yük hesabı için görevin <b>planlanan tarihleri</b>, <b>tahmini süresi</b> ve bir
            <b> sorumlusu</b> olmalı. Yalnız “Sorumlu” rolü kapasite tüketir.
          </p>
        </div>
      ) : (
        <div className="res-scroll">
          <table className="res-table">
            <thead>
              <tr>
                <th className="res-name-h">Kişi</th>
                {days.map((d) => {
                  const l = label(d);
                  return <th key={d} className="res-day-h"><span className="res-dow">{l.dow}</span><span className="mono">{l.d}</span></th>;
                })}
                <th className="res-tot-h">Top.</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.userId}>
                  <td className="res-name">
                    <span className="res-av">{p.name.charAt(0).toUpperCase()}</span>
                    <span className="res-nametxt">{p.name}</span>
                    {p.overDays > 0 ? (
                      <span className="res-overtag" title={`${p.overDays} gün kapasite aşımı`}>
                        <Icon name="alertTriangle" size={10} />{p.overDays}g
                      </span>
                    ) : null}
                  </td>
                  {p.days.map((d) => (
                    <td key={d.day} className={`res-cell ${d.over ? "over" : ""} ${d.hours === 0 ? "zero" : ""}`}
                      style={d.hours > 0 ? { ["--lvl" as string]: level(d.hours) } : undefined}
                      title={`${p.name} · ${label(d.day).month} · ${d.hours} sa${d.over ? ` (kapasite ${capacityHours ?? "?"} sa AŞILDI)` : ""}`}>
                      {d.hours > 0 ? (
                        <>
                          {/* Aşım RENK TEK BAŞINA anlatılamaz (CLAUDE.md kural 5):
                              hücrede ünlem işareti + kalın yazı + kenarlık birlikte. */}
                          {d.over ? <i className="res-over-mark" aria-hidden="true">!</i> : null}
                          <span className="mono">{d.hours % 1 === 0 ? d.hours : d.hours.toFixed(1)}</span>
                        </>
                      ) : null}
                    </td>
                  ))}
                  <td className="res-tot mono">{p.totalHours % 1 === 0 ? p.totalHours : p.totalHours.toFixed(1)}</td>
                </tr>
              ))}
              <tr className="res-sum">
                <td className="res-name">Ekip toplamı</td>
                {totals.map((t) => (
                  <td key={t.day} className="res-cell zero">
                    {t.hours > 0 ? <span className="mono">{t.hours % 1 === 0 ? t.hours : t.hours.toFixed(1)}</span> : null}
                  </td>
                ))}
                <td className="res-tot mono">
                  {(() => { const n = totals.reduce((a, b) => a + b.hours, 0); return n % 1 === 0 ? n : n.toFixed(1); })()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
